import { mapGetters } from 'vuex'
import { isDisplayScreen } from '@mixins/reloads-on-publish'

// public/index.html paints a display screen in the color it last showed while
// the app loads, so a reload doesn't flash white. It uses these same names
export const DISPLAY_BACKGROUND_KEY_PREFIX = 'resto:display-background:'
export const DISPLAY_BACKGROUND_CLASS = 'has-display-background'
export const DISPLAY_BACKGROUND_PROPERTY = '--display-background'
// Text that reads on the saved color, for anything shown before the layout
export const DISPLAY_TEXT_PROPERTY = '--display-text'

export function displayBackgroundKey(pathname) {
  return `${DISPLAY_BACKGROUND_KEY_PREFIX}${pathname}`
}

// getComputedStyle only reports rgba() for a color with some transparency
function isSeeThrough(color) {
  return !color || color === 'transparent' || /^rgba\(/.test(color.trim())
}

// The solid color behind an element: its own, or the nearest ancestor's when
// its own can be seen through
export function visibleBackgroundOf(el) {
  for (let node = el; node; node = node.parentElement) {
    const color = window.getComputedStyle(node).backgroundColor
    if (!isSeeThrough(color)) return color
  }
  return null
}

// Each channel of an rgb() or rgba() color, as getComputedStyle reports it
function rgbChannels(color) {
  const match = /^rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/.exec(color || '')
  return match ? match.slice(1, 4).map(Number) : null
}

// White on dark colors, Vuetify's dark text on light ones
export function readableTextColorOn(color) {
  const channels = rgbChannels(color)
  if (!channels) return null
  const [r, g, b] = channels
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness < 150 ? '#fff' : 'rgba(0, 0, 0, 0.87)'
}

// The saved color while public/index.html is painting it, otherwise null
export function activeDisplayBackground() {
  const root = document.documentElement
  if (!root.classList.contains(DISPLAY_BACKGROUND_CLASS)) return null
  return root.style.getPropertyValue(DISPLAY_BACKGROUND_PROPERTY).trim() || null
}

export function saveDisplayBackground(el, pathname = window.location.pathname) {
  // The fill paints the app wrappers too, so with it on, a transparent layout
  // would read back the saved color and never pick up a new one
  const root = document.documentElement
  const isFilling = root.classList.contains(DISPLAY_BACKGROUND_CLASS)
  if (isFilling) root.classList.remove(DISPLAY_BACKGROUND_CLASS)
  const color = visibleBackgroundOf(el)
  if (isFilling) root.classList.add(DISPLAY_BACKGROUND_CLASS)
  if (!color) return
  const key = displayBackgroundKey(pathname)
  try {
    if (window.localStorage.getItem(key) !== color) {
      window.localStorage.setItem(key, color)
    }
  } catch (e) {}
}

export function setDisplayTextColor() {
  const color = readableTextColorOn(activeDisplayBackground())
  if (!color) return
  document.documentElement.style.setProperty(DISPLAY_TEXT_PROPERTY, color)
}

export function stopDisplayBackgroundFill() {
  const root = document.documentElement
  root.classList.remove(DISPLAY_BACKGROUND_CLASS)
  root.style.removeProperty(DISPLAY_BACKGROUND_PROPERTY)
  root.style.removeProperty(DISPLAY_TEXT_PROPERTY)
}

// The fill also hides every loader, so it ends for good once the screen is
// anything other than a display
export default {
  computed: {
    ...mapGetters('layoutEditor', ['isShowingLayoutEditUI']),
    ...mapGetters('editor', ['isShowingEditUI']),
    hasLeftDisplayScreen() {
      // Nothing is known until the first route resolves
      if (!this.$route.matched.length) return false
      return !isDisplayScreen({
        routeName: this.$route.name,
        isShowingLayoutEditUI: this.isShowingLayoutEditUI,
        isShowingEditUI: this.isShowingEditUI,
      })
    },
  },
  created() {
    setDisplayTextColor()
  },
  watch: {
    hasLeftDisplayScreen: {
      immediate: true,
      handler(hasLeftDisplayScreen) {
        if (hasLeftDisplayScreen) stopDisplayBackgroundFill()
      },
    },
  },
}
